import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import Navbar from '../components/Navbar';

export default function BetaAccessPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate beta access code
      const { data, error: validateError } = await supabase.functions.invoke('validate-beta-code', {
        body: { code: accessCode }
      });

      if (validateError) throw validateError;

      if (data.valid) {
        // Update account with beta access
        const betaEndDate = new Date();
        betaEndDate.setDate(betaEndDate.getDate() + 30);

        const { error: updateError } = await supabase
          .from('accounts')
          .update({
            is_beta_tester: true,
            beta_access_end_date: betaEndDate.toISOString(),
            subscription_status: 'active' // Activate without payment
          })
          .eq('id', user?.id);

        if (updateError) throw updateError;

        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError('Invalid beta access code');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={signOut} />
      
      <div className="py-12">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow-lg rounded-lg p-8">
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
              Beta Tester Access
            </h1>
            
            {!success ? (
              <>
                <p className="text-gray-600 text-center mb-6">
                  Enter your beta access code to get 30 days of free access to FlowDocs
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Beta Access Code
                    </label>
                    <input
                      type="text"
                      id="accessCode"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                      placeholder="Enter your code"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={loading || !accessCode.trim()}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Validating...' : 'Activate Beta Access'}
                  </button>
                </form>
                
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Don't have a beta code?{' '}
                    <a href="/plans" className="text-blue-600 hover:text-blue-800">
                      View pricing plans
                    </a>
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Beta Access Activated!
                </h2>
                <p className="text-gray-600">
                  You now have 30 days of free access to FlowDocs.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Redirecting to dashboard...
                </p>
              </div>
            )}
          </div>
          
          {!success && (
            <div className="mt-8 bg-blue-50 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                What's included in beta access:
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• 30 days of full access</li>
                <li>• All Starter plan features</li>
                <li>• No credit card required</li>
                <li>• Help us improve with your feedback</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}